import { useEffect, useRef, useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { useFieldStep } from "@/components/wizard/use-field-step";

import { type CepResult, lookupCep } from "./cep";
import { DraftStorage } from "./draft-storage";
import { type Draft, FIELDS } from "./fields";
import { FichaFormSchemas, type FichaFormValues } from "./form";
import { maskFor } from "./masks";
import { FichaPayload } from "./payload";
import { downscalePhoto, photoDataUrl } from "./photo";
import { fieldError } from "./validation";

type Phase = "resume" | "welcome" | "form" | "send" | "done";

function formDraft({ accept: _accept, hp: _hp, ...draft }: FichaFormValues): Draft {
	return draft;
}

export function useFicha() {
	const stored = useRef(DraftStorage.load());
	const initialDraft = stored.current?.draft ?? {};
	const resumable = !!stored.current && DraftStorage.hasContent(initialDraft);
	const [phase, setPhase] = useState<Phase>(resumable ? "resume" : "welcome");
	const [cep, setCep] = useState<CepResult | null>(null);
	const methods = useForm<FichaFormValues>({
		resolver: arktypeResolver(FichaFormSchemas.form),
		mode: "onChange",
		defaultValues: {
			accept: false,
			hp: "",
			...initialDraft,
			photo: "",
		},
	});
	const { control, clearErrors, getValues, handleSubmit, reset, setError, setValue } = methods;
	const watched = useWatch({ control });
	const values = getValues();
	const draft = formDraft(values);

	const fieldStep = useFieldStep({
		active: phase === "form",
		fields: FIELDS,
		draft,
		setField: (key, value) => {
			setValue(key, value ?? "", { shouldDirty: true, shouldValidate: true });
		},
		maskFor,
		fieldError: (field) => fieldError(field, getValues(field.key) ?? ""),
		validateField: (field) => {
			const error = fieldError(field, getValues(field.key) ?? "");

			if (error !== null) {
				setError(field.key, { message: error });

				return false;
			}

			clearErrors(field.key);

			return true;
		},
		initialStep: stored.current?.step ?? 0,
		onComplete: () => setPhase("send"),
		onChange: (field, masked) => {
			if (field.kind === "cep") {
				setCep(null);
				lookupCep(masked).then((result) => result && setCep(result));
			}
		},
	});

	const photoPreview = draft.photo ? photoDataUrl({ data: draft.photo, mime: "image/jpeg" }) : null;
	const { mutateAsync, isPending } = useMutation(orpc.cadastro.submit.mutationOptions());
	const cepRehydrated = useRef(false);
	const submitting = useRef(false);

	useEffect(() => {
		if (phase !== "form" || cep !== null || cepRehydrated.current) {
			return;
		}

		if ((draft.cep ?? "").replaceAll(/\D/gu, "").length !== 8) {
			return;
		}

		cepRehydrated.current = true;
		lookupCep(draft.cep ?? "").then((result) => result && setCep(result));
	}, [phase, cep, draft.cep]);

	useEffect(() => {
		if (phase !== "form" && phase !== "send") {
			return;
		}

		DraftStorage.save({ draft, step: fieldStep.state.step });
	}, [watched, draft, fieldStep.state.step, phase]);

	const pickPhoto = async (file: File | undefined) => {
		if (file === undefined) {
			return;
		}

		await downscalePhoto(file)
			.then((photo) => {
				setValue("photo", photo.data, { shouldDirty: true, shouldValidate: true });
			})
			.catch(() => toast.error("Não foi possível usar essa foto. Escolha outra imagem."));
	};

	const clearPhoto = () => {
		setValue("photo", "", { shouldDirty: true, shouldValidate: true });
	};

	const submit = handleSubmit(async (values) => {
		if (submitting.current) {
			return;
		}

		submitting.current = true;

		await mutateAsync(
			FichaPayload.build({
				draft: formDraft(values),
				cep,
				accept: values.accept,
				hp: values.hp,
				photo: { data: values.photo, mime: "image/jpeg" },
			}),
		)
			.then(() => {
				DraftStorage.clear();
				setPhase("done");
			})
			.catch(() => {
				submitting.current = false;
				toast.error("Nao conseguimos enviar agora. Tenta de novo em instantes?");
			});
	});

	const goToForm = () => {
		setPhase("form");
	};

	const restart = () => {
		DraftStorage.clear();
		reset({ accept: false, hp: "", photo: "" });
		fieldStep.setStep(0);
		setCep(null);
		setPhase("welcome");
	};

	return {
		phase,
		...fieldStep.state,
		cep,
		photoPreview,
		draft,
		accept: values.accept,
		hp: values.hp,
		pending: isPending,
		actions: {
			...fieldStep.actions,
			pickPhoto,
			clearPhoto,
			submit,
			goToForm,
			restart,
			setAccept: (value: boolean) => setValue("accept", value, { shouldDirty: true }),
			setHp: (value: string) => setValue("hp", value, { shouldDirty: true }),
		},
	};
}

export type Ficha = ReturnType<typeof useFicha>;
